import type { Contact } from '~api/address_book';

import { Fragment, useRef } from 'react';

import { EmptyState, InteractiveTable, LoadingTableRows, MoreButton, Table } from '@ingrid/luterkort-react';

import { Ellipsis } from '~src/components/Ellipsis/Ellipsis';
import { TableAddressSecond } from '~src/components/TableAddress/TableAddress';
import { LocalizedCountry } from '~src/i18n/localized-country';
import { useTranslation } from '~src/i18n/use-translation';

import { ActionPanel } from './action-panel';
import { DetailsDrawer } from './details-drawer';
import { Menu } from './menu';

interface Props {
  loadRowCount: number;
  contacts: Contact[];
  isEmpty: boolean;
  loading: boolean;
}

export function ListTable(props: Props) {
  const { contacts, isEmpty, loadRowCount, loading } = props;

  const { highlightedKey } = InteractiveTable.useContext();
  const { t } = useTranslation();

  const contact = contacts.find(c => c.id === highlightedKey)!;
  const tableRef = useRef<HTMLTableElement>(null);

  if (isEmpty) {
    return <EmptyState title={t('addressBook.list.noMatchingContacts')} />;
  }

  return (
    <Fragment>
      <InteractiveTable.Table ref={tableRef}>
        <thead>
          <Table.Row>
            <Table.HeadCell css={{ width: '30px' }}>
              <InteractiveTable.HeadCheckbox />
            </Table.HeadCell>
            <Table.HeadCell css={{ width: '20%' }}>{t('common.name')}</Table.HeadCell>
            <Table.HeadCell css={{ width: '25%' }}>{t('common.address')}</Table.HeadCell>
            <Table.HeadCell css={{ width: '10%' }}>{t('common.postalCode')}</Table.HeadCell>
            <Table.HeadCell css={{ width: '10%' }}>{t('common.city')}</Table.HeadCell>
            <Table.HeadCell css={{ width: '10%' }}>{t('common.country')}</Table.HeadCell>
            <Table.HeadCell css={{ width: '10%' }}>{t('common.phone')}</Table.HeadCell>
            <Table.HeadCell css={{ width: '10%' }}>{t('common.email')}</Table.HeadCell>
            <Table.HeadCell css={{ width: '35px' }} />
          </Table.Row>
        </thead>
        <tbody>
          {contacts.map(contact => (
            <InteractiveTable.Row key={contact.id} value={contact.id!}>
              <Table.Cell>
                <InteractiveTable.Checkbox value={contact.id!} />
              </Table.Cell>
              <Table.Cell>
                <Ellipsis>{contact.address?.name ?? '-'}</Ellipsis>
              </Table.Cell>
              <Table.Cell>
                <Ellipsis>{contact.address?.address_lines?.[0] ?? '-'}</Ellipsis>
                {contact.address?.address_lines?.[1] && (
                  <TableAddressSecond>{contact.address?.address_lines[1]}</TableAddressSecond>
                )}
              </Table.Cell>
              <Table.Cell>
                <Ellipsis>{contact.address?.postal_code ?? '-'}</Ellipsis>
              </Table.Cell>
              <Table.Cell>
                <Ellipsis>{contact.address?.city ?? '-'}</Ellipsis>
              </Table.Cell>
              <Table.Cell>
                <Ellipsis>
                  {contact.address?.country ? <LocalizedCountry code={contact.address.country} /> : '-'}
                </Ellipsis>
              </Table.Cell>
              <Table.Cell>
                <Ellipsis>{contact.phone ?? '-'}</Ellipsis>
              </Table.Cell>
              <Table.Cell>
                <Ellipsis>{contact.email ?? '-'}</Ellipsis>{' '}
              </Table.Cell>
              <Table.Cell>
                <Menu contact={contact} trigger={<MoreButton size="sm" />} displayEditAction />
              </Table.Cell>
            </InteractiveTable.Row>
          ))}
          {loading && <LoadingTableRows tableRef={tableRef} rowCount={loadRowCount || 0} />}
        </tbody>
      </InteractiveTable.Table>

      <ActionPanel />

      <DetailsDrawer contact={contact} />
    </Fragment>
  );
}
